/** Helpers for Contact Master UI — derived display fields from GET /api/people rows. */

export function personMatchesQuery(person, needle) {
  const { email, phone } = deriveEmailPhone(person);
  const haystacks = [
    person.name,
    person.identifier,
    person.channel,
    person.channel_label,
    email,
    phone,
  ];
  return haystacks.some((value) => (value || "").toLowerCase().includes(needle));
}

export function fmtAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function isActiveWithin(iso, hours = 24) {
  if (!iso) return false;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return true;
  return ms <= hours * 3600 * 1000;
}

/** Active = touched within 7 days unless locally marked inactive. */
export function contactStatus(person, inactiveIds) {
  if (inactiveIds?.has?.(person.id)) return "inactive";
  return isActiveWithin(person.last_touch_at, 24 * 7) ? "active" : "inactive";
}

function messagingChannelPhone(id, looksEmail, looksPhone) {
  if (looksPhone) return id;
  if (looksEmail) return null;
  return id || null;
}

export function deriveEmailPhone(person) {
  const ch = (person.channel || "").toLowerCase();
  const id = (person.identifier || "").trim();
  const looksEmail = id.includes("@");
  const looksPhone = /^\+?[\d\s().-]{7,}$/.test(id);
  const email = looksEmail ? id : null;

  if (ch === "email" || ch === "gmail") {
    return { email, phone: null };
  }
  if (ch === "whatsapp" || ch === "sms" || ch === "telegram") {
    return { email, phone: messagingChannelPhone(id, looksEmail, looksPhone) };
  }
  return { email, phone: looksPhone ? id : null };
}

export function enrichPerson(person) {
  const { email, phone } = deriveEmailPhone(person);
  return { ...person, email, phone };
}

const AVATAR_TONES = {
  email: "bg-rose-500",
  gmail: "bg-rose-500",
  whatsapp: "bg-emerald-500",
  telegram: "bg-sky-500",
  linkedin: "bg-blue-600",
  linkedin_dm: "bg-blue-600",
  sms: "bg-amber-500",
};

export function avatarTone(channel) {
  return AVATAR_TONES[(channel || "").toLowerCase()] || "bg-slate-500";
}

export function channelBadgeTone(channel) {
  const ch = (channel || "").toLowerCase();
  if (ch === "email" || ch === "gmail") {
    return "text-rose-600 bg-rose-50 ring-rose-100";
  }
  if (ch === "whatsapp") return "text-emerald-700 bg-emerald-50 ring-emerald-100";
  if (ch === "telegram") return "text-sky-600 bg-sky-50 ring-sky-100";
  if (ch === "linkedin" || ch === "linkedin_dm") return "text-blue-700 bg-blue-50 ring-blue-100";
  if (ch === "sms") return "text-amber-700 bg-amber-50 ring-amber-100";
  return "text-slate-600 bg-slate-50 ring-slate-100";
}

export function computeContactStats(people) {
  const list = people || [];
  const channels = new Set(
    list.map((p) => (p.channel || "").toLowerCase()).filter(Boolean),
  );
  const conversations = list.reduce((sum, p) => sum + (Number(p.conversation_count) || 0), 0);
  const activeToday = list.filter((p) => isActiveWithin(p.last_touch_at, 24)).length;
  return {
    totalContacts: list.length,
    channelCount: channels.size,
    conversations,
    activeToday,
  };
}

/** Module lock — survives rapid double-clicks before React can disable the button. */
let peopleCsvExportInFlight = false;
const PEOPLE_CSV_EXPORT_COOLDOWN_MS = 600;

function logPeopleExport(event, detail = {}) {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.info(`[people-export] ${event}`, detail);
}

/**
 * Build and download Contact Master CSV.
 * Idempotent per short window: concurrent/duplicate calls are ignored.
 *
 * @returns {{ downloaded: boolean, ignored: boolean, rowCount: number, filename?: string }}
 */
export function exportPeopleCsv(people, { force = false } = {}) {
  const list = people || [];
  if (!list.length) {
    logPeopleExport("skipped_empty");
    return { downloaded: false, ignored: false, rowCount: 0 };
  }

  if (peopleCsvExportInFlight && !force) {
    logPeopleExport("duplicate_ignored", { rowCount: list.length });
    return { downloaded: false, ignored: true, rowCount: list.length };
  }

  peopleCsvExportInFlight = true;
  const filename = `contact-master-${new Date().toISOString().slice(0, 10)}.csv`;
  logPeopleExport("started", { rowCount: list.length, filename });

  try {
    const rows = [
      ["Name", "Channel", "Identifier", "Email", "Phone", "Conversations", "Last Activity"],
      ...list.map((p) => {
        const enriched = enrichPerson(p);
        return [
          enriched.name || "",
          enriched.channel_label || enriched.channel || "",
          enriched.identifier || "",
          enriched.email || "",
          enriched.phone || "",
          String(enriched.conversation_count || 0),
          enriched.last_touch_at || "",
        ];
      }),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.click();
    URL.revokeObjectURL(url);
    logPeopleExport("completed", { rowCount: list.length, filename });
    return { downloaded: true, ignored: false, rowCount: list.length, filename };
  } catch (error) {
    logPeopleExport("failed", { message: error?.message || String(error) });
    throw error;
  } finally {
    window.setTimeout(() => {
      peopleCsvExportInFlight = false;
      logPeopleExport("lock_released");
    }, PEOPLE_CSV_EXPORT_COOLDOWN_MS);
  }
}

/** Test helper — reset in-flight lock between unit tests. */
export function __resetPeopleCsvExportLockForTests() {
  peopleCsvExportInFlight = false;
}
